import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  MouseEventHandler,
} from 'react'
import _ from 'lodash'
import stringSimilarity from 'string-similarity'
import ScreenLoading from 'components/ScreenLoading'
import Modal from 'components/Modal'

interface IOption {
  label: string
  value: any
  icon?: string
  disabled?: boolean
}

export const makeOption = (value: string): IOption => {
  return { label: value, value }
}

interface IDropDownOption {
  value: any
  selected: boolean
  highlighted: boolean
  onClick: MouseEventHandler<HTMLLIElement>
  inputValue: any
  icon?: string
  disabled?: boolean
}

const DropDownOption = ({
  value,
  selected,
  highlighted,
  onClick,
  inputValue,
  icon,
  disabled,
}: IDropDownOption) => {
  const elementRef = useRef<HTMLLIElement | null>(null)

  useEffect(() => {
    if (highlighted && elementRef?.current?.parentElement) {
      const miss =
        elementRef.current.offsetTop -
        elementRef.current.parentElement.scrollTop
      if (miss > 140) {
        elementRef.current.parentElement.scrollTop =
          elementRef.current.offsetTop - 130
      } else if (miss < 0) {
        elementRef.current.parentElement.scrollTop =
          elementRef.current.offsetTop
      }
    }
  }, [highlighted])

  return (
    <li
      className={`option-item ${selected ? 'selected' : ''} ${
        highlighted ? 'highlighted' : ''
      } ${disabled ? 'disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      ref={elementRef}
    >
      {icon && <span className="material-icons option-icon">{icon}</span>}
      <span className="option-label">{value}</span>
      {selected && (
        <span className="material-icons check-icon">check</span>
      )}
    </li>
  )
}

interface IDropDownField {
  label?: string
  options: IOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  inputRef?: any
  hasError?: boolean
  error?: string
  success?: string
  info?: string
  loadingOptions?: boolean
  filterWithSimilarity?: boolean
  style?: any
  noBold?: boolean
  fixedChild?: React.ReactElement
  staticLabel?: string
  placeholder?: string
  icon?: string
  helperText?: string
  required?: boolean
  clearable?: boolean
  searchable?: boolean
}

function DropDownField({
  label = '',
  options,
  value,
  onChange,
  disabled = false,
  inputRef,
  hasError = false,
  error = '',
  success = '',
  info = '',
  loadingOptions = false,
  filterWithSimilarity = false,
  noBold = false,
  style = {},
  staticLabel = '',
  fixedChild,
  placeholder = '',
  icon,
  helperText,
  required = false,
  clearable = false,
  searchable = true,
}: IDropDownField) {
  const selectedOption = _.find(options, ['value', value])
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(
    selectedOption ? selectedOption.label : ''
  )
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isMobile, setIsMobile] = useState(false)
  const fieldRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleClickOutside = useCallback(
    (event) => {
      if (disabled) {
        return
      }

      if (fieldRef.current && !fieldRef.current.contains(event.target)) {
        setOpen(false)
      }
      document.removeEventListener('mousedown', handleClickOutside)
    },
    [disabled, setOpen]
  )

  useEffect(() => {
    if (!disabled) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [disabled, handleClickOutside])

  useEffect(() => {
    setFilteredOptions(options)
    // eslint-disable-next-line
  }, [JSON.stringify(options)])

  const handleClickDropDown = () => {
    if (disabled) {
      return
    }

    setFilteredOptions(options)
    setHighlightedIndex(_.findIndex(options, ['value', value]))

    setOpen(!open)
    if (!open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
  }

  const handleChooseOption = (optionIndex: number) => {
    if (disabled) {
      return
    }

    const option = options[optionIndex]
    setInputValue(option.label)
    onChange(option.value)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return

    setInputValue('')
    onChange('')
    setFilteredOptions(options)
  }

  const handleTextChange = (inputValue: any) => {
    const filtered = options.filter((option) => {
      const containsSubString = option.label
        .toLowerCase()
        .includes(inputValue.toLowerCase())

      if (filterWithSimilarity) {
        return (
          containsSubString ||
          stringSimilarity.compareTwoStrings(option.label, inputValue) > 0.4
        )
      } else {
        return containsSubString
      }
    })
    setFilteredOptions(filtered)
    setInputValue(inputValue)
    setHighlightedIndex(0)
  }

  const handleKeyDown = (event: any) => {
    if (!open) {
      if (event.keyCode === 13 || event.keyCode === 32) {
        // Enter or Space
        event.preventDefault()
        setOpen(true)
      }
      return
    }

    if (event.keyCode === 40) {
      // Arrow Down
      event.preventDefault()
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      )
    } else if (event.keyCode === 38) {
      // Arrow Up
      event.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (event.keyCode === 13) {
      // Enter
      event.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const option = filteredOptions[highlightedIndex]
        if (!option.disabled) {
          handleChooseOption(options.indexOf(option))
          setOpen(false)
        }
      }
    } else if (event.keyCode === 27) {
      // Escape
      event.preventDefault()
      setOpen(false)
      setInputValue(selectedOption ? selectedOption.label : '')
    }
  }

  const isValueInOptions = _.find(options, ['value', value])
  const showError = (hasError || !isValueInOptions || error) && !open
  const showSuccess = success && !open
  const showInfo = info && !open

  // Use staticLabel for both label and display value if provided
  const displayLabel = staticLabel || label
  const displayValue = staticLabel || selectedOption?.label || ''

  const dropdownContent = (
    <>
      {searchable && (
        <div className="search-container">
          <span className="material-icons search-icon">search</span>
          <input
            autoFocus
            value={inputValue}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="search-input"
            type="text"
            placeholder="Search..."
            ref={inputRef}
            aria-label="Search options"
          />
        </div>
      )}

      {loadingOptions ? (
        <ul className="options-list">
          <li style={{ padding: 40, textAlign: 'center' }}>
            <ScreenLoading />
          </li>
        </ul>
      ) : filteredOptions.length > 0 ? (
        <ul className="options-list">
          {filteredOptions.map((option, optionIndex) => {
            const originalIndex = options.indexOf(option)
            return (
              <DropDownOption
                key={optionIndex}
                value={option.label}
                selected={option.value === value}
                highlighted={optionIndex === highlightedIndex}
                onClick={() => handleChooseOption(originalIndex)}
                inputValue={inputValue}
                icon={option.icon}
                disabled={option.disabled}
              />
            )
          })}
          {fixedChild && fixedChild}
        </ul>
      ) : (
        <ul className="options-list">
          <li className="no-options">No options found</li>
        </ul>
      )}
    </>
  )

  if (isMobile && open) {
    return (
      <Modal
        show={true}
        onHide={() => setOpen(false)}
        className="modern-select-modal"
      >
        <div className="modern-select-modal-content">
          <div className="modal-header">
            <h2>{displayLabel || 'Select Option'}</h2>
            <button className="close-button" onClick={() => setOpen(false)}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="modal-body">
            {dropdownContent}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <div
      ref={fieldRef}
      className={`modern-select-field ${open ? 'open' : ''} ${
        disabled ? 'disabled' : ''
      } ${showError ? 'error' : ''} ${showSuccess ? 'success' : ''} ${
        showInfo ? 'info' : ''
      }`}
      style={style}
    >
      {displayLabel && (
        <label className="modern-select-label">
          {displayLabel}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}

      <div
        className="modern-select-container"
        onClick={handleClickDropDown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        {icon && !open && (
          <span className="material-icons modern-select-icon">{icon}</span>
        )}

        <div className="modern-select-value">
          {selectedOption?.icon && !open && (
            <span className="material-icons option-icon">
              {selectedOption.icon}
            </span>
          )}
          <span className={displayValue ? '' : 'placeholder'}>
            {displayValue || placeholder || 'Select...'}
          </span>
        </div>

        <div className="modern-select-actions">
          {clearable && value && !disabled && (
            <button
              className="clear-button"
              onClick={handleClear}
              aria-label="Clear selection"
              type="button"
            >
              <span className="material-icons">close</span>
            </button>
          )}
          <span
            className={`material-icons arrow ${open ? 'rotate' : ''}`}
            aria-hidden="true"
          >
            keyboard_arrow_down
          </span>
        </div>
      </div>

      {open && !isMobile && (
        <div className="modern-select-dropdown" role="listbox">
          {dropdownContent}
        </div>
      )}

      {(showError || showSuccess || showInfo || helperText) && (
        <div className="modern-select-message">
          {showError && (error || 'Invalid selection') ? (
            <span className="error-message">{error || 'Invalid selection'}</span>
          ) : showSuccess && success ? (
            <span className="success-message">{success}</span>
          ) : showInfo && info ? (
            <span className="info-message">{info}</span>
          ) : helperText ? (
            <span className="helper-text">{helperText}</span>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default React.memo(DropDownField)
